<template>
  <section class="page" data-testid="page-memory">
    <header class="page-header">
      <div><h1>记忆</h1><p>RAG 检索、来源解释和单条治理。</p></div>
    </header>

    <div class="toolbar">
      <button class="button" :class="{ primary: tab === 'search' }" @click="tab = 'search'">检索</button>
      <button class="button" :class="{ primary: tab === 'governance' }" @click="openGovernance">治理</button>
      <button class="button" :disabled="!canCopyCurrentView" @click="copyCurrentView">复制当前视图</button>
      <button class="button" :disabled="loading" @click="() => loadSummary()">刷新质量概览</button>
    </div>

    <div class="metrics-grid" v-if="summary.total !== undefined">
      <MetricCard label="总记忆" :value="summary.total || 0" />
      <MetricCard label="Active 记忆" :value="summary.active || 0" />
      <MetricCard label="Disabled/Deleted" :value="summary.risk_flags?.disabled_or_deleted || 0" />
      <MetricCard label="索引失败" :value="summary.risk_flags?.index_failed || 0" />
      <MetricCard label="缺来源" :value="summary.risk_flags?.missing_source || 0" />
      <MetricCard label="隐私过滤" :value="summary.risk_flags?.privacy_filtered || 0" />
      <MetricCard label="高重要" :value="summary.risk_flags?.high_importance || 0" />
    </div>
    <div class="metrics-grid" v-if="searched">
      <MetricCard label="召回结果" :value="results.length" />
      <MetricCard label="精确原文" :value="searchMetrics.lexical" />
      <MetricCard label="平均分数" :value="searchMetrics.avgScore" />
      <MetricCard label="可治理结果" :value="searchMetrics.governable" />
    </div>
    <p v-if="operationSummary" class="save-result">{{ operationSummary }}</p>
    <p v-if="operationError" class="error-text">{{ operationError }}</p>

    <div v-if="tab === 'search'" class="panel">
      <div class="toolbar">
        <input v-model="query" placeholder="输入检索问题" @keyup.enter="search" />
        <select v-model="personaSelection" @change="applySearchPersonaSelection">
          <option value="">全部/默认</option>
          <option v-for="name in personas" :key="name" :value="name">{{ name }}</option>
          <option value="__custom">自定义</option>
        </select>
        <input v-if="personaSelection === '__custom'" v-model="persona" placeholder="自定义人设" />
        <button class="button primary" :disabled="loading || !query.trim()" @click="search">{{ loading ? "检索中..." : "检索" }}</button>
      </div>
      <div v-if="searched && !loading && !results.length" class="empty">
        没有召回 active 记忆。可能该人设还没有重建记忆，或相关记忆已被禁用/删除。
      </div>
      <div v-for="row in results" :key="row.memory_id || row.doc" class="memory-card">
        <div class="memory-card__head">
          <strong>{{ row.memory_id || "无 ID 记忆" }}</strong>
          <span class="status-badge">{{ row.status || "active" }}</span>
          <span>分数 {{ row.score ?? "-" }}</span>
          <span v-if="row.why_recalled?.lexical_match" class="status-badge">精确原文</span>
        </div>
        <pre>{{ row.doc || row.document }}</pre>
        <div class="memory-meta">
          <span>来源：{{ sourceLabel(row.source) }}</span>
          <span>关键词 {{ row.why_recalled?.keyword_score ?? row.keyword_score ?? 0 }}</span>
          <span>语义 {{ row.why_recalled?.semantic_score ?? row.semantic_score ?? 0 }}</span>
          <span v-if="row.why_recalled?.retrieval_sources?.length">召回源 {{ row.why_recalled.retrieval_sources.join(", ") }}</span>
          <span>重要度 {{ row.importance ?? row.metadata?.importance ?? "-" }}</span>
        </div>
        <div class="recall-explain">
          <strong>{{ recallVerdict(row) }}</strong>
          <span>{{ recallDetail(row) }}</span>
        </div>
        <div class="actions" v-if="row.memory_id">
          <button class="button" @click="loadItem(row.memory_id)">查看/编辑</button>
          <button class="button" @click="disableItem(row.memory_id)">禁用</button>
          <button class="button" @click="copyMemory(row)">复制证据</button>
        </div>
      </div>
    </div>

    <div v-if="tab === 'governance'" class="panel">
      <div class="toolbar">
        <select v-model="governancePersonaSelection" @change="applyGovernancePersonaSelection">
          <option value="">全部/默认</option>
          <option v-for="name in personas" :key="name" :value="name">{{ name }}</option>
          <option value="__custom">自定义</option>
        </select>
        <input v-if="governancePersonaSelection === '__custom'" v-model="governance.persona" placeholder="自定义人设" />
        <select v-model="governance.status">
          <option value="">全部状态</option>
          <option value="active">active</option>
          <option value="disabled">disabled</option>
          <option value="deleted">deleted</option>
        </select>
        <button class="button" :class="{ primary: governanceRiskFilter === 'all' }" @click="governanceRiskFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: governanceRiskFilter === 'problem' }" @click="governanceRiskFilter = 'problem'">问题记忆</button>
        <button class="button" :class="{ primary: governanceRiskFilter === 'high' }" @click="governanceRiskFilter = 'high'">高重要</button>
        <button class="button" :class="{ primary: governanceRiskFilter === 'source' }" @click="governanceRiskFilter = 'source'">缺来源</button>
        <input v-model="governance.q" placeholder="搜索内容/来源" @keyup.enter="() => listItems()" />
        <button class="button primary" :disabled="loading" @click="() => listItems()">刷新</button>
      </div>
      <div v-if="!filteredItems.length && !loading" class="empty">
        当前筛选条件下没有记忆。清空筛选或先从导入/聊天记录重建记忆。
      </div>
      <table v-if="filteredItems.length" class="data-table">
        <thead>
          <tr>
            <th>状态</th>
            <th>人设</th>
            <th>内容</th>
            <th>来源</th>
            <th>索引</th>
            <th>质量</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filteredItems" :key="item.memory_id">
            <td><span class="status-badge" :class="memoryStatusClass(item)">{{ item.status }}</span></td>
            <td>{{ item.persona_name }}</td>
            <td class="cell-main">{{ item.document }}</td>
            <td>{{ sourceLabel(item.source) }}</td>
            <td>{{ item.embedding_status }}</td>
            <td>
              <div class="quality-cell">
                <span>重要度 {{ item.importance ?? "-" }}</span>
                <span>{{ memoryQualityLabel(item) }}</span>
              </div>
            </td>
            <td>
              <div class="row-actions">
                <button class="button" @click="selectItem(item)">详情</button>
                <button class="button" @click="copyMemory(item)">复制</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="selected" class="panel">
      <div class="page-header">
        <div><h2>记忆详情</h2><p>{{ selected.memory_id }}</p></div>
        <div class="actions">
          <button class="button" @click="copyMemory(selected)">复制详情</button>
          <button class="button" @click="selected = null">关闭</button>
        </div>
      </div>
      <div class="split">
        <div>
          <div class="memory-profile">
            <div><span>状态</span><strong>{{ selected.status || "-" }}</strong></div>
            <div><span>人设</span><strong>{{ selected.persona_name || "-" }}</strong></div>
            <div><span>索引</span><strong>{{ selected.embedding_status || "-" }}</strong></div>
            <div><span>来源</span><strong>{{ sourceLabel(selected.source) }}</strong></div>
          </div>
          <label>内容</label>
          <textarea v-model="edit.document" />
          <label>重要度</label>
          <input v-model.number="edit.importance" type="number" min="1" max="5" />
          <label>修改原因</label>
          <input v-model="edit.reason" placeholder="例如：用户确认这条记忆不准确" />
          <div class="actions">
            <button class="button primary" :disabled="loading || !edit.document.trim()" @click="saveItem">保存并重建索引</button>
            <button v-if="selected.status === 'active'" class="button" @click="disableItem(selected.memory_id)">禁用</button>
            <button v-if="selected.status !== 'active'" class="button" @click="enableItem(selected.memory_id)">启用</button>
            <button class="button danger" @click="deleteItem(selected.memory_id)">删除</button>
          </div>
        </div>
        <div>
          <h3>来源解释</h3>
          <pre>{{ sourceDetail }}</pre>
          <h3>审计历史</h3>
          <pre>{{ JSON.stringify(events, null, 2) }}</pre>
        </div>
      </div>
    </div>

    <div class="panel">
      <h2>重建与清空</h2>
      <div class="toolbar">
        <input v-model="rebuildPath" placeholder="uploads 或 chat_logs 下的文件路径" />
        <select v-model="rebuildPersonaSelection" @change="applyRebuildPersonaSelection">
          <option value="">沿用检索人设/默认</option>
          <option v-for="name in personas" :key="name" :value="name">{{ name }}</option>
          <option value="__custom">自定义</option>
        </select>
        <input v-if="rebuildPersonaSelection === '__custom'" v-model="rebuildPersona" placeholder="自定义人设" />
        <button class="button primary" :disabled="loading || !rebuildPath.trim()" @click="rebuild">重建记忆</button>
        <button class="button" :disabled="loading" @click="reindex">重建召回索引</button>
        <button class="button danger" :disabled="loading" @click="clearAll">清空当前人设记忆</button>
      </div>
      <p>重建召回索引用于修复旧向量空间不一致，不会改变记忆文本。</p>
      <pre>{{ operationResult || "尚未执行" }}</pre>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api, postJson } from "../api/client";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const tab = ref<"search" | "governance">("search");
const query = ref("");
const persona = ref("");
const personaSelection = ref("");
const personas = ref<string[]>([]);
const summary = ref<any>({});
const results = ref<any[]>([]);
const items = ref<any[]>([]);
const selected = ref<any | null>(null);
const events = ref<any[]>([]);
const sourceDetail = ref("");
const edit = ref({ document: "", importance: 1, reason: "" });
const governance = ref({ persona: "", status: "active", q: "" });
const governanceRiskFilter = ref<"all" | "problem" | "high" | "source">("all");
const governancePersonaSelection = ref("");
const rebuildPath = ref("");
const rebuildPersona = ref("");
const rebuildPersonaSelection = ref("");
const operationResult = ref("");
const operationSummary = ref("");
const operationError = ref("");
const { copyJson } = useClipboardResult(operationSummary, "浏览器不允许复制，请手动选择内容");
const loading = ref(false);
const searched = ref(false);
const canCopyCurrentView = computed(() => {
  if (tab.value === "search") return searched.value && !!results.value.length;
  return !!filteredItems.value.length;
});
const searchMetrics = computed(() => {
  const scores = results.value.map((item) => Number(item.score ?? item.metadata?.score)).filter(Number.isFinite);
  const lexical = results.value.filter((item) => item.why_recalled?.lexical_match).length;
  const avgScore = scores.length ? (scores.reduce((sum, item) => sum + item, 0) / scores.length).toFixed(2) : "-";
  return {
    lexical,
    avgScore,
    governable: results.value.filter((item) => item.memory_id).length
  };
});
const filteredItems = computed(() => {
  return items.value.filter((item) => {
    if (governanceRiskFilter.value === "problem" && !isProblemMemory(item)) return false;
    if (governanceRiskFilter.value === "high" && Number(item.importance || 0) < 4) return false;
    if (governanceRiskFilter.value === "source" && hasSource(item)) return false;
    return true;
  });
});
const governancePersonaRef = computed({
  get: () => governance.value.persona,
  set: (value: string) => {
    governance.value.persona = value;
  }
});

function syncPersonaSelection(value: string, selection: { value: string }) {
  if (!value) {
    selection.value = "";
  } else if (personas.value.includes(value)) {
    selection.value = value;
  } else {
    selection.value = "__custom";
  }
}

function applySearchPersonaSelection() {
  if (personaSelection.value === "__custom") return;
  persona.value = personaSelection.value;
}

function applyGovernancePersonaSelection() {
  if (governancePersonaSelection.value === "__custom") return;
  governancePersonaRef.value = governancePersonaSelection.value;
}

function applyRebuildPersonaSelection() {
  if (rebuildPersonaSelection.value === "__custom") return;
  rebuildPersona.value = rebuildPersonaSelection.value;
}

function sourceLabel(source: any) {
  if (!source) return "未知";
  return [source.type, source.ref].filter(Boolean).join(" / ") || source.preview || "未知";
}

function hasSource(item: any) {
  const source = item?.source || item?.metadata?.source;
  return !!(source && (source.type || source.ref || source.preview));
}

function isProblemMemory(item: any) {
  return item.status !== "active" || item.embedding_status === "failed" || item.embedding_status === "missing" || !hasSource(item);
}

function memoryStatusClass(item: any) {
  if (item.status === "active" && item.embedding_status !== "failed") return "ok";
  if (item.status === "deleted") return "error";
  return "degraded";
}

function memoryQualityLabel(item: any) {
  if (!hasSource(item)) return "缺来源";
  if (item.embedding_status === "failed") return "索引失败";
  if (Number(item.importance || 0) >= 4) return "高重要";
  return "正常";
}

function recallVerdict(row: any) {
  if (row.why_recalled?.lexical_match) return "原文命中";
  const semantic = Number(row.why_recalled?.semantic_score ?? row.semantic_score ?? 0);
  const keyword = Number(row.why_recalled?.keyword_score ?? row.keyword_score ?? 0);
  if (semantic >= keyword) return "语义召回";
  return "关键词召回";
}

function recallDetail(row: any) {
  const sources = row.why_recalled?.retrieval_sources || [];
  const status = row.status || "active";
  const importance = row.importance ?? row.metadata?.importance ?? "-";
  return `状态 ${status}，重要度 ${importance}，召回源 ${sources.length ? sources.join(", ") : "-"}`;
}

async function copyMemory(item: any) {
  await copyJson({ item, source: selected.value?.memory_id === item?.memory_id ? sourceDetail.value : undefined, events: selected.value?.memory_id === item?.memory_id ? events.value : undefined }, "记忆证据已复制");
}

async function copyCurrentView() {
  const payload = tab.value === "search"
    ? {
        mode: "search",
        query: query.value,
        persona: persona.value || "all",
        metrics: searchMetrics.value,
        results: results.value
      }
    : {
        mode: "governance",
        filter: { ...governance.value, risk: governanceRiskFilter.value },
        summary: summary.value,
        items: filteredItems.value.slice(0, 100)
      };
  await copyJson(payload, "当前记忆视图已复制");
}

async function loadPersonas() {
  try {
    const payload = await api<{ personas: string[] }>("/api/personas");
    personas.value = payload.personas || [];
    syncPersonaSelection(persona.value, personaSelection);
    syncPersonaSelection(governance.value.persona, governancePersonaSelection);
    syncPersonaSelection(rebuildPersona.value, rebuildPersonaSelection);
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "人设列表加载失败";
  }
}

async function loadSummary(setBusy = true, announce = true) {
  const target = governance.value.persona || persona.value || "";
  if (setBusy) loading.value = true;
  operationError.value = "";
  try {
    const payload = await api<{ summary: any }>(`/api/memory/summary?persona=${encodeURIComponent(target)}`);
    summary.value = payload.summary || {};
    if (announce) operationSummary.value = "记忆质量概览已刷新";
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "记忆质量概览加载失败";
    toast.push(operationError.value, "error");
  } finally {
    if (setBusy) loading.value = false;
  }
}

async function loadInitialData() {
  await Promise.all([loadPersonas(), loadSummary(false, false)]);
}

async function search() {
  if (!query.value.trim()) return;
  loading.value = true;
  searched.value = true;
  operationError.value = "";
  operationSummary.value = "";
  try {
    const payload = await api<{ results: any[] }>(`/api/memory/search?q=${encodeURIComponent(query.value)}&persona=${encodeURIComponent(persona.value)}&limit=20`);
    results.value = payload.results || [];
    operationSummary.value = `检索完成，召回 ${results.value.length} 条记忆。`;
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "记忆检索失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function openGovernance() {
  tab.value = "governance";
  if (!items.value.length) {
    await listItems();
  } else {
    await loadSummary();
  }
}

async function listItems(announce = true) {
  loading.value = true;
  operationError.value = "";
  try {
    const params = new URLSearchParams({
      persona: governance.value.persona,
      status: governance.value.status,
      q: governance.value.q,
      limit: "100"
    });
    const payload = await api<{ items: any[] }>(`/api/memory/items?${params.toString()}`);
    items.value = payload.items || [];
    await loadSummary(false, false);
    if (announce) operationSummary.value = `已加载 ${items.value.length} 条记忆，当前筛选匹配 ${filteredItems.value.length} 条。`;
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "记忆列表加载失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function loadItem(memoryId: string) {
  tab.value = "governance";
  loading.value = true;
  operationError.value = "";
  try {
    const payload = await api<{ item: any; events: any[] }>(`/api/memory/items/${encodeURIComponent(memoryId)}`);
    await selectItem(payload.item, payload.events || []);
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "记忆详情加载失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function selectItem(item: any, auditEvents: any[] = []) {
  selected.value = item;
  edit.value = { document: item.document || "", importance: item.importance || 1, reason: "" };
  events.value = auditEvents;
  operationError.value = "";
  try {
    const source = await api<any>(`/api/memory/items/${encodeURIComponent(item.memory_id)}/source`);
    sourceDetail.value = JSON.stringify(source, null, 2);
    events.value = source.events || auditEvents;
  } catch (err) {
    sourceDetail.value = "来源解释加载失败";
    operationError.value = err instanceof Error ? err.message : "来源解释加载失败";
    toast.push(operationError.value, "error");
  }
}

async function saveItem() {
  if (!selected.value) return;
  loading.value = true;
  operationError.value = "";
  try {
    const payload = await api<any>(`/api/memory/items/${encodeURIComponent(selected.value.memory_id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(edit.value)
    });
    selected.value = payload.item;
    operationSummary.value = payload.index_status === "indexed" ? "已保存并更新召回索引。" : "已保存，但索引暂不可用。";
    toast.push("记忆已保存");
    await listItems(false);
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "保存失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function disableItem(memoryId: string) {
  loading.value = true;
  operationError.value = "";
  try {
    const payload = await postJson<any>(`/api/memory/items/${encodeURIComponent(memoryId)}/disable`, { reason: "admin disabled" });
    selected.value = payload.item;
    operationSummary.value = "记忆已禁用，不会再进入召回。";
    toast.push("记忆已禁用");
    await listItems(false);
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "禁用记忆失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function enableItem(memoryId: string) {
  loading.value = true;
  operationError.value = "";
  try {
    const payload = await postJson<any>(`/api/memory/items/${encodeURIComponent(memoryId)}/enable`, { reason: "admin enabled" });
    selected.value = payload.item;
    operationSummary.value = payload.index_status === "indexed" ? "记忆已启用并可召回。" : "记忆已启用，但索引暂不可用。";
    toast.push("记忆已启用");
    await listItems(false);
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "启用记忆失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function deleteItem(memoryId: string) {
  if (!window.confirm("确定删除这条记忆？删除后默认只做软删除。")) return;
  loading.value = true;
  operationError.value = "";
  try {
    const payload = await api<any>(`/api/memory/items/${encodeURIComponent(memoryId)}`, { method: "DELETE" });
    selected.value = payload.item;
    operationSummary.value = "记忆已软删除，不会再进入召回。";
    toast.push("记忆已删除");
    await listItems(false);
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "删除记忆失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function rebuild() {
  loading.value = true;
  operationError.value = "";
  operationSummary.value = "";
  try {
    const payload = await postJson<any>("/api/memory/rebuild", { path: rebuildPath.value, persona: rebuildPersona.value || persona.value || undefined });
    operationResult.value = JSON.stringify(payload, null, 2);
    operationSummary.value = `已为 ${payload.persona || "默认人设"} 导入 ${payload.imported || 0} 条消息，当前记忆 ${payload.memory_count || 0} 条。`;
    toast.push("记忆已重建");
    await listItems(false);
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "重建失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function reindex() {
  const target = rebuildPersona.value || persona.value || "";
  loading.value = true;
  operationError.value = "";
  operationSummary.value = "";
  try {
    const payload = await postJson<any>("/api/memory/reindex", { persona: target, limit: 0 });
    operationResult.value = JSON.stringify(payload, null, 2);
    operationSummary.value = `已重建 ${payload.indexed || 0}/${payload.total || 0} 条召回索引，失败 ${payload.failed || 0} 条。`;
    toast.push(payload.ok ? "召回索引已重建" : "召回索引部分失败");
    if (query.value.trim()) await search();
    await listItems(false);
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "重建召回索引失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

async function clearAll() {
  const target = rebuildPersona.value || persona.value || "";
  if (!window.confirm(`确定清空${target || "默认人设"}的记忆？`)) return;
  loading.value = true;
  operationError.value = "";
  operationSummary.value = "";
  try {
    const suffix = target ? `?persona=${encodeURIComponent(target)}` : "";
    const payload = await api<any>(`/api/memory/all${suffix}`, { method: "DELETE" });
    operationResult.value = JSON.stringify(payload, null, 2);
    operationSummary.value = `已清空 ${payload.persona || "默认人设"} 的记忆，剩余 ${payload.memory_count || 0} 条。`;
    results.value = [];
    await listItems(false);
    toast.push("记忆已清空");
  } catch (err) {
    operationError.value = err instanceof Error ? err.message : "清空失败";
    toast.push(operationError.value, "error");
  } finally {
    loading.value = false;
  }
}

onMounted(loadInitialData);
</script>

<style scoped>
.memory-card {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 14px;
  margin-top: 12px;
}

.memory-card__head,
.memory-meta,
.row-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.memory-card__head {
  margin-bottom: 10px;
}

.memory-meta {
  color: var(--muted);
  font-size: 13px;
  margin: 10px 0;
}

.recall-explain {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px;
  margin: 10px 0;
}

.recall-explain strong,
.recall-explain span {
  display: block;
}

.recall-explain span {
  color: var(--muted);
  margin-top: 4px;
}

.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.cell-main {
  max-width: 420px;
}

.quality-cell {
  display: grid;
  gap: 4px;
  min-width: 90px;
}

.quality-cell span:last-child {
  color: var(--muted);
  font-size: 12px;
}

.memory-profile {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.memory-profile div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
}

.memory-profile span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.memory-profile strong {
  display: block;
  margin-top: 5px;
  word-break: break-all;
}
</style>
